import React from 'react';
import { Box, Typography, Paper, CircularProgress, List, ListItem, ListItemText, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#e6eaf0',
    borderRadius: 18,
    boxSizing: 'border-box',
  },
  drawer: {
    width: 280,
    flexShrink: 0,
    backgroundColor: '#f7faff',
    borderRadius: 18,
    border: '1px solid #dce8f5',
    padding: theme.spacing(1.5),
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
    boxShadow: '0 10px 24px rgba(0, 45, 85, 0.08)',
  },
  drawerTitle: {
    color: '#002D55',
    fontWeight: 700,
    padding: theme.spacing(1, 2),
    fontSize: 'clamp(0.9rem, 0.95vw, 1.05rem)',
    borderBottom: '1px solid #dce8f5',
    marginBottom: theme.spacing(1),
  },
  listItem: {
    borderRadius: 14,
    marginBottom: theme.spacing(0.5),
    cursor: 'pointer',
    border: '1px solid transparent',
    '&:hover': {
      backgroundColor: '#edf5fc',
      borderColor: '#dce8f5',
    },
  },
  listItemSelected: {
    backgroundColor: '#ffffff !important',
    border: '1px solid #dce8f5',
    boxShadow: '0 8px 20px rgba(0, 45, 85, 0.08)',
    '& $listPrimaryText': {
      color: '#FF7F27',
    },
    '&:hover': {
      backgroundColor: '#ffffff !important',
    },
  },
  listPrimaryText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#002D55',
  },
  listSecondaryText: {
    fontSize: '0.75rem',
    color: '#64748B',
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 300px)',
    minWidth: 0,
  },
  section: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f7faff',
    borderRadius: 18,
    border: '1px solid #dce8f5',
    boxShadow: 'none',
  },
  sectionTitle: {
    color: '#FF7F27',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: 'clamp(0.76rem, 0.82vw, 0.92rem)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  content: {
    color: '#324158',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: 'clamp(0.72rem, 0.8vw, 0.88rem)',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#64748B',
  },
  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0.5, 0.5, 0),
  },
  statusHeaderText: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  statusCounter: {
    fontSize: 'clamp(0.62rem, 0.7vw, 0.81rem)',
    fontWeight: 700,
    color: '#002D55',
    backgroundColor: '#f5f5f5',
    border: '1px solid #dddddd',
    borderRadius: 14,
    padding: '4px 12px',
    whiteSpace: 'nowrap',
  },
}));

const EpicrisisView = ({ selectedPatient, userToken }) => {
  const classes = useStyles();
  const [epicrisisList, setEpicrisisList] = React.useState([]);
  const [selectedEpicrisis, setSelectedEpicrisis] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchEpicrisis = async () => {
      if (!selectedPatient || !selectedPatient.treatment_id || !userToken) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `https://api.virtus-one.com/journal/epicrisis/${selectedPatient.treatment_id}/list/`,
          {
            headers: {
              'Authorization': `Token ${userToken}`,
              'Accept': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch epicrisis: ${response.status}`);
        }

        const data = await response.json();
        // Sort by most recent first (assuming created_at or updated_at field)
        const sortedData = Array.isArray(data) 
          ? data.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
          : [];
        
        setEpicrisisList(sortedData);
        // Default to the most recent epicrisis
        setSelectedEpicrisis(sortedData.length > 0 ? sortedData[0] : null);
      } catch (err) {
        console.error('[EpicrisisView] Error fetching epicrisis:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEpicrisis();
  }, [selectedPatient?.treatment_id, userToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">Error loading epicrisis: {error}</Typography>
      </Box>
    );
  }

  if (epicrisisList.length === 0) {
    return (
      <Box p={2}>
        <Paper className={classes.section}>
          <Typography className={classes.emptyState}>
            No epicrisis available for this treatment.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.drawer}>
        <Typography className={classes.drawerTitle}>
          Epicrisis Reports ({epicrisisList.length})
        </Typography>
        <Divider />
        <List component="nav">
          {epicrisisList.map((epicrisis, index) => (
            <ListItem
              key={epicrisis.id || index}
              className={`${classes.listItem} ${selectedEpicrisis?.id === epicrisis.id ? classes.listItemSelected : ''}`}
              onClick={() => setSelectedEpicrisis(epicrisis)}
            >
              <ListItemText
                primary={
                  <Typography className={classes.listPrimaryText}>
                    Report #{epicrisisList.length - index}
                  </Typography>
                }
                secondary={
                  <Typography className={classes.listSecondaryText}>
                    {formatDate(epicrisis.updated_at || epicrisis.created_at)}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box className={classes.contentArea}>
        <Box className={classes.statusHeader}>
          <Typography className={classes.statusHeaderText}>
            Epicrisis
          </Typography>
          <span className={classes.statusCounter}>
            {epicrisisList.length} report{epicrisisList.length === 1 ? '' : 's'}
          </span>
        </Box>
        {selectedEpicrisis && (
          <>
            {selectedEpicrisis.referral_reason && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Referral Reason
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedEpicrisis.referral_reason}
                </Typography>
              </Paper>
            )}

            {selectedEpicrisis.status && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Status
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedEpicrisis.status}
                </Typography>
              </Paper>
            )}

            {selectedEpicrisis.gp_suggestion && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  GP Suggestion
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedEpicrisis.gp_suggestion}
                </Typography>
              </Paper>
            )}

            {selectedEpicrisis.physiotherapy_diagnosis && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Clinical Examination & Findings
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedEpicrisis.physiotherapy_diagnosis}
                </Typography>
              </Paper>
            )}

            {selectedEpicrisis.treatment_summary && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Treatment Summary
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedEpicrisis.treatment_summary}
                </Typography>
              </Paper>
            )}

            {selectedEpicrisis.patient_education && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Patient Education
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedEpicrisis.patient_education}
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default EpicrisisView;
